// Action Cable provides the framework to deal with WebSockets in Rails.
// You can generate new channels where WebSocket features live using the `rails generate channel` command.

import { createConsumer } from "@rails/actioncable"

const consumer = createConsumer()

// ── Zombie-connection guard ────────────────────────────────────────────
// Problem: after laptop sleep the browser WebSocket can be in a "zombie"
// state — readyState === OPEN but the TCP connection is silently dead.
// ActionCable does not detect this on its own for several minutes, leaving
// the page stuck with stale data and a broken layout.
//
// How this works:
//   1. We track when the tab was hidden (hiddenAt).
//   2. On visibilitychange → visible: if the tab was hidden for > 30 s
//      (laptop sleep, OS backgrounding) the connection is likely a zombie,
//      so we force-close the WebSocket.
//   3. On the OS-level "online" event (WiFi reconnects after waking) we
//      also force a reconnect.
//   4. Closing with allowReconnect: true (ActionCable default) causes
//      ActionCable to reopen the WebSocket and re-subscribe all channels
//      automatically, which in turn triggers each channel's connected()
//      callback and the per-channel data-refresh logic already in place.
//
// No server changes are required.

let _tabHiddenAt = null
let _lastForcedReconnectAt = 0

function forceReconnect() {
  const now = Date.now()
  if (now - _lastForcedReconnectAt < 5000) return
  _lastForcedReconnectAt = now
  try {
    const conn = consumer.connection
    if (!conn) return
    const ws = conn.webSocket
    if (ws) {
      const state = ws.readyState
      if (state === WebSocket.CONNECTING) return  // already reconnecting — don't interrupt
      if (state === WebSocket.CLOSING)   return  // already closing — let ActionCable handle it
      if (state === WebSocket.CLOSED) {
        conn.open()  // nudge ActionCable to reopen
        return
      }
      // OPEN — potential zombie. Force close; conn.close() defaults to allowReconnect: true,
      // so ActionCable's ConnectionMonitor will reopen the socket automatically.
      conn.close()
    }
    // Safety net: if the ConnectionMonitor is slow to reopen, nudge it.
    setTimeout(function () {
      try {
        const ws2 = consumer.connection && consumer.connection.webSocket
        if (!ws2 || ws2.readyState === WebSocket.CLOSED) {
          consumer.connection.open()
        }
      } catch (_) {}
    }, 1500)
  } catch (_) {
    // Silently ignore — ActionCable's own monitor will reconnect eventually.
  }
}

document.addEventListener("visibilitychange", function () {
  if (document.visibilityState === "hidden") {
    _tabHiddenAt = Date.now()
    return
  }
  // Tab is visible again. If hidden > 30 s (laptop sleep / OS backgrounding)
  // the WebSocket is likely a zombie — force a clean reconnect.
  if (_tabHiddenAt !== null && Date.now() - _tabHiddenAt > 30000) {
    forceReconnect()
  }
  _tabHiddenAt = null
})

// OS-level network restoration (e.g. WiFi reconnects after waking).
// Debounced 300 ms: the "online" event can fire multiple times in rapid
// succession (flaky WiFi, brief drop during wake), so we coalesce them
// into a single reconnect attempt — the same pattern used in the channel
// files to avoid concurrent subscription reloads.
let _onlineDebounce = null
window.addEventListener("online", function () {
  if (_onlineDebounce) clearTimeout(_onlineDebounce)
  _onlineDebounce = setTimeout(function () {
    _onlineDebounce = null
    forceReconnect()
  }, 300)
})

// ── End zombie guard ───────────────────────────────────────────────────

window.addEventListener("pagehide", function () {
  try {
    consumer.disconnect()
  } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent("monet:actioncable:disconnected"))
  } catch (_) {}
})

window.addEventListener("pageshow", function (ev) {
  if (!ev.persisted) return
  // Page restored from bfcache — reopen the ActionCable connection.
  setTimeout(function () {
    try { consumer.connection.open() } catch (_) {}
  }, 0)
})

export default consumer
